import { useCallback, useEffect, useRef, useState } from "react";
import { getApiErrorMessage } from "../../../../utils";
import type { DownloadCourse, DownloadParts } from "./downloads.types";
import { downloadsLockRefusal, takeDownloadsLock } from "./downloadsLock";
import type { HeldLock } from "./downloadsLock";
import { runDownloads } from "./downloadsRunner";
import type { DownloadRowState } from "./rowResult";

// `starting`: the lock is being taken. `finished` stays until the next listing.
export type RunPhase = "idle" | "starting" | "running" | "finished";

const QUEUED: DownloadRowState = { kind: "queued" };

const LOST_LEASE_MESSAGE =
  "Se perdió el turno de esta descarga y se detuvo. Los cursos que faltan quedan sin descargar.";

export function useDownloadRun() {
  const [phase, setPhase] = useState<RunPhase>("idle");
  // The courses of the current run, which the page shows instead of the list.
  const [queue, setQueue] = useState<DownloadCourse[]>([]);
  // What the current run asked for, which its summary keeps showing.
  const [runParts, setRunParts] = useState<DownloadParts | null>(null);
  const [rows, setRows] = useState<Record<number, DownloadRowState>>({});
  const [error, setError] = useState<string | null>(null);
  const stopRequested = useRef(false);
  const heldLock = useRef<HeldLock | null>(null);
  const mounted = useRef(true);
  const active = phase === "starting" || phase === "running";

  // Leaving the page stops new courses from starting, the ones in flight
  // finish, and the turn is handed back.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      stopRequested.current = true;
      heldLock.current?.release();
    };
  }, []);

  useEffect(() => {
    if (!active) return;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [active]);

  const start = useCallback(
    async (courses: DownloadCourse[], parts: DownloadParts) => {
      stopRequested.current = false;
      setError(null);
      setPhase("starting");

      // A run that cannot take its courses does not start.
      let lock: HeldLock;
      try {
        lock = await takeDownloadsLock(
          courses.map((course) => course.courseId),
          () => {
            stopRequested.current = true;
            setError(LOST_LEASE_MESSAGE);
          },
        );
      } catch (err) {
        setError(
          downloadsLockRefusal(err) ??
            getApiErrorMessage(
              err,
              "No se pudo tomar el turno de esta descarga.",
            ),
        );
        setPhase("idle");
        return;
      }
      // Nobody is left to give the turn back, and its heartbeat would never stop.
      if (!mounted.current) {
        lock.release();
        return;
      }
      heldLock.current = lock;

      setQueue(courses);
      setRunParts(parts);
      setRows(
        Object.fromEntries(courses.map((course) => [course.courseId, QUEUED])),
      );
      setPhase("running");
      try {
        await runDownloads({
          courses,
          parts,
          onUpdate: (courseId, state) =>
            setRows((previous) => ({ ...previous, [courseId]: state })),
          shouldStop: () => stopRequested.current,
        });
      } catch (err) {
        setError(
          getApiErrorMessage(
            err,
            "La descarga se interrumpió por un error inesperado.",
          ),
        );
      } finally {
        lock.release();
        heldLock.current = null;
        setPhase("finished");
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setPhase("idle");
    setQueue([]);
    setRunParts(null);
    setRows({});
    setError(null);
  }, []);

  return { phase, queue, runParts, rows, error, start, reset };
}
